<script lang="ts">
  import MarathonStamp from "./stamps/MarathonStamp.svelte";
  import IronmanStamp from "./stamps/IronmanStamp.svelte";
  import BerlinToAmsterdam from "./stamps/BerlinToAmsterdam.svelte";
  import TourDeCalifornia from "./stamps/TourDeCalifornia.svelte";
  import TourDeFrance from "./stamps/TourDeFrance.svelte";
  import CrossCanada from "./stamps/CrossCanada.svelte";
  import CircumferenceOfMoon from "./stamps/CircumferenceOfMoon.svelte";
  import LondonToCapetown from "./stamps/LondonToCapetown.svelte";
  import PanAmericanHighway from "./stamps/PanAmericanHighway.svelte";
  import CircumferenceOfEarth from "./stamps/CircumferenceOfEarth.svelte";
  import { kmToMile, round } from "@/Util/utils";

  export let year: number;
  export let distanceKm: number;
  export let distanceUnit: string; // 'km' or 'mi'
  export let revealed: boolean;
  export let useMobileLayout: boolean;
  
  type Achievement = { name: string; km: number; title: string };
  const ACHIEVEMENTS: Achievement[] = [
    { name: "marathon", km: 42, title: 'completed the marathon' },
    { name: "ironman", km: 180, title: 'completed the ironman distance' },
    { name: "berlin to amsterdam", km: 530, title: 'made it from berlin to amsterdam' },
    { name: "tour de california", km: 1100, title: 'completed the tour de california' },
    { name: "tour de france", km: 3500, title: 'completed the tour de france' },
    { name: "across Canada", km: 7600, title: 'made it across Canada' },
    { name: "circumference of the moon", km: 10921, title: 'made it all the way around the moon' },
    { name: "london to capetown", km: 19000, title: 'made it from London to Capetown' },
    { name: "pan american highway", km: 30000, title: 'traveled the pan american highway' },
    { name: "circumference of the earth", km: 40075, title: 'made it all the way around the earth' },
  ];

  // Normalize to kilometers
  $: metAchievements = ACHIEVEMENTS.filter((a) => distanceKm >= a.km);
  $: topAchieved = metAchievements.length ? metAchievements[metAchievements.length - 1] : null;
  $: topTimes = topAchieved ? round(distanceKm / topAchieved.km, 1) : 0;
  $: nextAchievement = ACHIEVEMENTS.find((a) => a.km > distanceKm) || null;

  function getDisplayDistance(distance, unit) {
    return round(unit == 'mi' ? kmToMile(distance ?? 0) : (distance ?? 0));
  }

  const formatDistance = (n: number, unit: string) => {
    let val = getDisplayDistance(n, unit);
    const abs = Math.abs(val);
    const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
    return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: digits }).format(val)}\u00A0${unit}`;
  };
  $: totalDistanceDisplay = formatDistance(distanceKm ?? 0, distanceUnit ?? 'km');
  const timesLabel = (n: number) => (n === 1 ? 'time' : 'times');
  
</script>

<div class="passport-paper relative w-full rounded-xl border border-[#e7deca] shadow-sm overflow-hidden">
  <!-- Decorative watermark rosette (subtle) -->
  <div class="watermark" aria-hidden="true"></div>

  <span class="p-2 px-4 uppercase font-semibold text-xl text-black tracking-widest">{year} stamps</span>

  <!-- Content layer -->
  <div class="w-full flex flex-col gap-2 z-10">
    {#if useMobileLayout}
      <div class='grid grid-cols-2 grid-rows-5 gap-0'>
        {#each Array(10) as a, i}
          <div class='passport-stamp w-full h-20 text-black rounded-lg flex items-center justify-center text-sm font-medium tracking-wide uppercase' class:passport-stamp-in={revealed} style="--delay: {500 + (300 + 25 * (i + (10 - metAchievements.length))) * i}ms; --rotation: {Math.random() * 30 - 15}deg;">
            {#if metAchievements[i]?.name == "marathon"}
              <MarathonStamp />
            {:else if metAchievements[i]?.name == "ironman"}
              <IronmanStamp />
            {:else if metAchievements[i]?.name == "berlin to amsterdam"}
              <BerlinToAmsterdam distanceText={formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "tour de california"}
              <TourDeCalifornia distanceText={formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "tour de france"}
              <TourDeFrance distanceText={formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "across Canada"}
              <CrossCanada distanceText={formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "circumference of the moon"}
              <CircumferenceOfMoon distanceText={formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "london to capetown"}
              <LondonToCapetown distanceText={formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "pan american highway"}
              <PanAmericanHighway distanceText={formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "circumference of the earth"}
              <CircumferenceOfEarth distanceText={formatDistance(metAchievements[i].km, distanceUnit)} />
            {/if}
          </div>
        {/each}
      </div>
    {:else}
      <div class='grid grid-cols-8 grid-rows-3 gap-0'>
        {#each Array(10) as a, i}
          {#if i == 0 || i == 7}
            <div class='col-span-1'/>
          {/if}
          <div class='passport-stamp w-full h-20 text-black rounded-lg flex items-center justify-center text-sm font-medium tracking-wide uppercase col-span-2' class:passport-stamp-in={revealed} style="--delay: {500 + (300 + 25 * (i + (10 - metAchievements.length))) * i}ms; --rotation: {Math.random() * 30 - 15}deg;">
            {#if metAchievements[i]?.name == "marathon"}
              <MarathonStamp />
            {:else if metAchievements[i]?.name == "ironman"}
              <IronmanStamp />
            {:else if metAchievements[i]?.name == "berlin to amsterdam"}
              <BerlinToAmsterdam distanceText={'~' + formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "tour de california"}
              <TourDeCalifornia distanceText={'~' + formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "tour de france"}
              <TourDeFrance distanceText={'~' + formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "across Canada"}
              <CrossCanada distanceText={'~' + formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "circumference of the moon"}
              <CircumferenceOfMoon distanceText={formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "london to capetown"}
              <LondonToCapetown distanceText={'~' + formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "pan american highway"}
              <PanAmericanHighway distanceText={'~' + formatDistance(metAchievements[i].km, distanceUnit)} />
            {:else if metAchievements[i]?.name == "circumference of the earth"}
              <CircumferenceOfEarth distanceText={formatDistance(metAchievements[i].km, distanceUnit)} />
            {/if}
          </div>
          {#if i == 2 || i == 9}
            <div class='col-span-1'/>
          {/if}
        {/each}
      </div>
    {/if}
    <div class="border-t p-4 border-dashed border-sky-300/60">
      <div class='flex flex-col text-sm gap-1'>
        <div class='flex flex-row gap-2 items-center'>
          <span class="uppercase tracking-widest text-xs text-sky-800/80 font-semibold">Total Distance</span>
          <span class="uppercase font-semibold text-xl text-black tracking-widest">{totalDistanceDisplay}</span>
        </div>
        {#if topAchieved}
          <span class='uppercase text-black/80 tracking-tight'>• You {topAchieved.title} {topTimes} {timesLabel(topTimes)}</span>
        {/if}
        {#if nextAchievement}
          <span class='uppercase text-black/80 tracking-tight'>• You almost {nextAchievement.title} - {formatDistance(nextAchievement.km, distanceUnit)}</span>
        {:else}
          <span class='uppercase text-black/80 tracking-tight'>• All achievements completed!</span>
        {/if}
      </div>
    </div>
  </div>
</div>

<style>
  /* Soft passport paper texture using layered gradients and a subtle noise film */
  .passport-paper {
    --paper: #f8f6ef; /* warm off-white */
    --ink-a: 165, 198, 225; /* soft blue */
    --ink-b: 255, 215, 161; /* soft peach */
    background-color: var(--paper);
    position: relative;
    /* Base texture + micro-lines reminiscent of passport microprint */
    background-image:
      radial-gradient(140% 120% at 20% 20%, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.38) 55%, rgba(0,0,0,0.06) 100%),
      repeating-linear-gradient(32deg, rgba(var(--ink-a), 0.06) 0 1px, transparent 1px 7px),
      repeating-linear-gradient(-32deg, rgba(var(--ink-b), 0.06) 0 1px, transparent 1px 7px);
  }

  /* Fine paper grain */
  .passport-paper::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image: radial-gradient(rgba(0,0,0,0.2) 1px, transparent 1px);
    background-size: 3px 3px;
    opacity: 0.45;
    mix-blend-mode: multiply;
  }

  /* Guilloche-style rosette watermark */
  .passport-paper .watermark {
    position: absolute;
    left: clamp(0.75rem, 3vw, 1.25rem);
    top: clamp(0.5rem, 3vw, 1rem);
    width: clamp(10rem, 28vw, 20rem);
    height: clamp(10rem, 28vw, 20rem);
    border-radius: 50%;
    pointer-events: none;
    opacity: 0.25;
    filter: blur(0.4px);
    mix-blend-mode: multiply;
    background-image: repeating-conic-gradient(
      from 0deg,
      rgba(var(--ink-a), 0.08) 0deg 10deg,
      rgba(var(--ink-b), 0.08) 10deg 20deg
    );
    /* punch an inner ring and soften edges */
    -webkit-mask-image: radial-gradient(circle at 50% 50%, transparent 0% 28%, black 30% 62%, transparent 70% 100%);
            mask-image: radial-gradient(circle at 50% 50%, transparent 0% 28%, black 30% 62%, transparent 70% 100%);
  }

  /* Ensure inner grid sits comfortably on the paper */
  .passport-paper .grid {
    gap: 0.5rem;
  }

  .passport-stamp {
    opacity: 0;
  }

  .passport-stamp-in { animation: stampdrop 720ms cubic-bezier(0.22, 0.95, 0.4, 1) var(--delay, 500ms) both; }
  @keyframes stampdrop {
    0% { transform: translateY(-24px) scale(0.92) rotate(calc(var(--rotation, -6deg) - 8deg)); opacity: 0; filter: drop-shadow(0 0 0 rgba(0,0,0,0.0)); }
    45% { transform: translateY(0) scale(1.08) rotate(var(--rotation, -6deg)); opacity: 1; filter: drop-shadow(0 8px 18px rgba(0,0,0,0.52)); }
    70% { transform: translateY(0) scale(0.96, 0.98) rotate(calc(var(--rotation, -6deg) - 1deg)); opacity: 0.9; }
    100% { transform: translateY(0) scale(1.0) rotate(var(--rotation, -6deg)); opacity: 0.9; }
  }
</style>
